import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { toast } from "sonner";
import { Printer, Send, X, Link2, Download, Package } from "lucide-react";
import { fetcher, asList, imgUrl } from "@/lib/api";
import { useShortlist } from "@/context/ShortlistContext";
import { QuoteDialog } from "@/components/QuoteDialog";
import { isSampleable } from "@/lib/samples";
import { MaskTitle } from "@/components/MaskTitle";
import { Eyebrow } from "@/components/Reveal";

const inp = "w-full h-11 bg-[#13141C] border border-[#D4AF37]/25 px-4 text-sm text-[#F5F3EF] placeholder:text-[#A09E97] focus:outline-none focus:border-[#D4AF37]";

export default function SelectionPage() {
  const local = useShortlist();
  const { toggle, clear, setMeta, addMany } = local;
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sharedParam = params.get("items");
  const shared = sharedParam !== null;
  const ids = shared ? sharedParam.split(",").filter(Boolean) : local.ids;
  const client = shared ? params.get("client") || "" : local.client;
  const project = shared ? params.get("project") || "" : local.project;
  const shareQs = new URLSearchParams({ items: ids.join(","), ...(client && { client }), ...(project && { project }) }).toString();
  const copyLink = async () => {
    const url = `${window.location.origin}/selection?${shareQs}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied. Anyone with it can view this selection.");
    } catch {
      window.prompt("Copy this link", url);
    }
  };
  const saveShared = () => {
    addMany(ids);
    if (client && !local.client) setMeta("client", client);
    if (project && !local.project) setMeta("project", project);
    toast.success("Added to your selection");
    navigate("/selection");
  };
  const [quote, setQuote] = useState(false);
  const [samples, setSamples] = useState(false);
  const { data: catData } = useSWR("/categories", fetcher);
  const { data: itemData } = useSWR("/items", fetcher);
  const items = asList(itemData).filter((i) => ids.includes(i.id));
  const sampleItems = items.filter(isSampleable);
  const groups = asList(catData).map((c) => ({ c, items: items.filter((i) => i.category === c.slug) })).filter((g) => g.items.length);

  return (
    <main className="pt-32 mx-auto max-w-[1100px] px-5 sm:px-10 min-h-screen">
      <Eyebrow>{shared ? "Shared selection" : "Your selection"} · {items.length} pieces</Eyebrow>
      <MaskTitle text="Material selection" className="mt-5 font-display font-light text-5xl sm:text-7xl text-[#F5F3EF]" />
      {shared ? (
        <p className="mt-4 text-[#A09E97] max-w-lg" data-testid="selection-shared-note">
          Someone shared this shortlist with you{client ? `, prepared for ${client}` : ""}{project ? ` (${project})` : ""}. Print it, request a quote, or save it to your own selection.
        </p>
      ) : (
        <p className="mt-4 text-[#A09E97] max-w-lg">Shortlist hardware, stone, finishes and appliances, then print your own A4 selection sheet, share a link, or send it to us for a quote. Saved on this device.</p>
      )}

      {items.length === 0 ? (
        <div className="mt-16 border border-dashed border-[#D4AF37]/30 p-12 text-center" data-testid="selection-empty">
          <p className="font-display italic text-3xl text-[#A09E97]">Nothing selected yet.</p>
          <Link to="/#catalogue" className="mt-6 inline-block nav-link font-mono-j text-xs uppercase tracking-[0.25em] text-[#E5C158]">Browse the catalogue</Link>
        </div>
      ) : (
        <>
          {!shared && (
            <div className="mt-12 grid sm:grid-cols-2 gap-4">
              <input value={client} onChange={(e) => setMeta("client", e.target.value)} placeholder="Your name (shown on the sheet)" className={inp} data-testid="selection-client-input" />
              <input value={project} onChange={(e) => setMeta("project", e.target.value)} placeholder="Project / address" className={inp} data-testid="selection-project-input" />
            </div>
          )}
          <div className="mt-6 flex flex-wrap gap-3">
            <button onClick={() => setQuote(true)} data-testid="selection-quote-btn"
              className="h-12 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-7 text-sm font-medium text-[#0D0D11] hover:bg-[#E5C158] transition-colors"><Send className="w-4 h-4" /> Request a quote</button>
            {sampleItems.length > 0 && (
              <button onClick={() => setSamples(true)} data-testid="selection-samples-btn"
                className="h-12 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 px-7 text-sm text-[#E5C158] hover:bg-[#D4AF37]/10 transition-colors"><Package className="w-4 h-4" /> Request samples ({sampleItems.length})</button>
            )}
            <Link to={shared ? `/print?${shareQs}` : "/print?selection=1"} data-testid="selection-print-btn"
              className="h-12 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 px-7 text-sm text-[#E5C158] hover:bg-[#D4AF37]/10 transition-colors"><Printer className="w-4 h-4" /> Print A4 sheet</Link>
            {shared ? (
              <button onClick={saveShared} data-testid="selection-save-shared-btn"
                className="h-12 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 px-7 text-sm text-[#E5C158] hover:bg-[#D4AF37]/10 transition-colors"><Download className="w-4 h-4" /> Save to my selection</button>
            ) : (
              <>
                <button onClick={copyLink} data-testid="selection-share-btn"
                  className="h-12 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 px-7 text-sm text-[#E5C158] hover:bg-[#D4AF37]/10 transition-colors"><Link2 className="w-4 h-4" /> Copy share link</button>
                <button onClick={clear} data-testid="selection-clear-btn" className="h-12 px-5 text-sm text-[#A09E97] hover:text-[#F5F3EF]">Clear all</button>
              </>
            )}
          </div>
          <div className="mt-14 space-y-12" data-testid="selection-list">
            {groups.map(({ c, items: gi }) => (
              <section key={c.id}>
                <h2 className="font-display text-3xl text-[#F5F3EF] border-b border-[#D4AF37]/20 pb-3">{c.name}</h2>
                {gi.map((i) => (
                  <div key={i.id} className="flex items-center gap-4 py-4 border-b border-[#D4AF37]/10" data-testid={`selection-item-${i.id}`}>
                    <Link to={`/item/${i.id}`} className="shrink-0"><img src={imgUrl(i.images?.[0])} alt="" className="w-20 h-20 object-cover border border-[#D4AF37]/20" /></Link>
                    <div className="flex-1 min-w-0">
                      <Link to={`/item/${i.id}`} className="font-display text-2xl text-[#F5F3EF] hover:text-[#E5C158] transition-colors">{i.name}</Link>
                      <p className="text-sm text-[#A09E97] truncate">{[i.supplier, i.code].filter(Boolean).join(" · ")}</p>
                    </div>
                    {!shared && (
                      <button onClick={() => toggle(i.id)} aria-label="Remove" data-testid={`selection-remove-${i.id}`}
                        className="w-10 h-10 grid place-items-center rounded-full border border-[#D4AF37]/25 text-[#A09E97] hover:text-red-400 transition-colors"><X className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
              </section>
            ))}
          </div>
        </>
      )}
      <QuoteDialog open={quote} onClose={() => setQuote(false)} items={items} />
      <QuoteDialog open={samples} onClose={() => setSamples(false)} mode="samples" items={sampleItems} />
    </main>
  );
}
